import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    buildDefaultFilterPreferencesFromMetadata,
    getFilterPreferencesStorageKey,
    loadFilterPreferencesFromStorage,
    moveFilterFieldBetweenLists,
    normalizeMetadataFiltersArray,
    resolveMetadataFiltersWithPreferences,
    saveFilterPreferencesToStorage,
} from "helpers/metadataFilterHelpers";
import {
    saveFilterPreferencesUserSetting,
} from "model/UserSettingModel";
import { useUserSettingsSync } from "libs/context/HeaderSettingsSyncContext";

const FILTER_PREFS_SYNC_DEBOUNCE_MS = 400;

const FilterPreferencesContext = createContext(null);

export const FilterPreferencesProvider = ({ children }) => {
    const { syncGeneration } = useUserSettingsSync();
    const [preferencesByPageKey, setPreferencesByPageKey] = useState({});
    const serverSyncTimeoutByPageKeyRef = useRef({});

    useEffect(
        () => () => {
            Object.values(serverSyncTimeoutByPageKeyRef.current).forEach(clearTimeout);
            serverSyncTimeoutByPageKeyRef.current = {};
        },
        []
    );

    /** Re-read localStorage after the shared UserSetting sync. */
    useEffect(() => {
        if (syncGeneration < 1) return;
        setPreferencesByPageKey({});
    }, [syncGeneration]);

    const syncPreferencesToServer = useCallback((pageKey, preferences) => {
        if (!pageKey) return;

        const timeouts = serverSyncTimeoutByPageKeyRef.current;
        if (timeouts[pageKey]) {
            clearTimeout(timeouts[pageKey]);
        }

        timeouts[pageKey] = setTimeout(() => {
            delete timeouts[pageKey];
            saveFilterPreferencesUserSetting(
                getFilterPreferencesStorageKey(pageKey),
                preferences
            );
        }, FILTER_PREFS_SYNC_DEBOUNCE_MS);
    }, []);

    const persistPreferences = useCallback(
        (pageKey, preferences, { syncToServer = true } = {}) => {
            if (!pageKey) return;
            saveFilterPreferencesToStorage(pageKey, preferences);
            setPreferencesByPageKey((previous) => ({
                ...previous,
                [pageKey]: preferences,
            }));
            if (syncToServer) {
                syncPreferencesToServer(pageKey, preferences);
            }
        },
        [syncPreferencesToServer]
    );

    const getResolvedFilters = useCallback(
        (pageKey, metadataFilters = []) => {
            const normalizedPageKey = String(pageKey ?? "").trim();
            const normalizedFilters = normalizeMetadataFiltersArray(metadataFilters);
            if (!normalizedPageKey || !normalizedFilters.length) {
                return resolveMetadataFiltersWithPreferences(normalizedFilters);
            }

            const fromMemory = preferencesByPageKey[normalizedPageKey];
            const stored =
                fromMemory || loadFilterPreferencesFromStorage(normalizedPageKey);
            return resolveMetadataFiltersWithPreferences(normalizedFilters, stored);
        },
        [preferencesByPageKey]
    );

    const getPreferences = useCallback(
        (pageKey, metadataFilters = []) => {
            const resolved = getResolvedFilters(pageKey, metadataFilters);
            return {
                toolbarFieldOrder: resolved.toolbarFieldOrder,
                advancedFieldOrder: resolved.advancedFieldOrder,
            };
        },
        [getResolvedFilters]
    );

    const handleFilterDragEnd = useCallback(
        (pageKey, dragResult, metadataFilters = []) => {
            const normalizedPageKey = String(pageKey ?? "").trim();
            if (!normalizedPageKey || !dragResult?.destination) return;

            const current = getPreferences(normalizedPageKey, metadataFilters);
            const next = moveFilterFieldBetweenLists(current, {
                field: dragResult.draggableId,
                sourceListId: dragResult.source.droppableId,
                destinationListId: dragResult.destination.droppableId,
                sourceIndex: dragResult.source.index,
                destinationIndex: dragResult.destination.index,
            });
            persistPreferences(normalizedPageKey, next);
            return next;
        },
        [getPreferences, persistPreferences]
    );

    const resetFilterPreferences = useCallback(
        (pageKey, metadataFilters = []) => {
            const normalizedPageKey = String(pageKey ?? "").trim();
            if (!normalizedPageKey) return null;
            const defaults = buildDefaultFilterPreferencesFromMetadata(metadataFilters);
            persistPreferences(normalizedPageKey, defaults);
            return defaults;
        },
        [persistPreferences]
    );

    const value = useMemo(
        () => ({
            preferencesByPageKey,
            getResolvedFilters,
            getPreferences,
            handleFilterDragEnd,
            resetFilterPreferences,
        }),
        [
            preferencesByPageKey,
            getResolvedFilters,
            getPreferences,
            handleFilterDragEnd,
            resetFilterPreferences,
        ]
    );

    return (
        <FilterPreferencesContext.Provider value={value}>
            {children}
        </FilterPreferencesContext.Provider>
    );
};

export const useFilterPreferences = (pageKey = "", metadataFilters = []) => {
    const context = useContext(FilterPreferencesContext);
    const resolvedPageKey = String(pageKey ?? "").trim();
    const normalizedFilters = useMemo(
        () => normalizeMetadataFiltersArray(metadataFilters),
        [metadataFilters]
    );
    const storedPrefs = context?.preferencesByPageKey?.[resolvedPageKey];

    const resolved = useMemo(() => {
        if (!context || !resolvedPageKey) {
            return resolveMetadataFiltersWithPreferences(normalizedFilters);
        }
        return context.getResolvedFilters(resolvedPageKey, normalizedFilters);
    }, [context, resolvedPageKey, normalizedFilters, storedPrefs]);

    if (!context) {
        return {
            ...resolved,
            handleFilterDragEnd: () => {},
            resetFilterPreferences: () => {},
        };
    }

    return {
        ...resolved,
        handleFilterDragEnd: (dragResult) =>
            context.handleFilterDragEnd(resolvedPageKey, dragResult, normalizedFilters),
        resetFilterPreferences: () =>
            context.resetFilterPreferences(resolvedPageKey, normalizedFilters),
    };
};

export default FilterPreferencesContext;
